import React, { useMemo, useRef, useState } from "react";
import styles from "./seatPacksTab.module.css";
// The section shell, the status banner and the button weights are the sibling tab's — reused rather
// than re-declared so the two screens can't drift apart.
import buyStyles from "./buyCreditsTab.module.css";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import {
  SeatSubscriptionDto,
  useCancelSeatSubscriptionMutation,
  useListSeatSubscriptionsQuery,
} from "../../../../services/api/featureCreditPackagesApi";
import {
  priceLabel,
  seatPackState,
  seatPackStatusLabel,
  sortSeatPacks,
} from "../../../../utils/seatSubscriptions";
import { mapSeatCancelError } from "../../../../utils/featureCreditPackages";
import { formatDateInIsrael } from "../../../../utils/utils";
import Loader from "../../../loader/loader";
import Modal from "../../modal/Modal";
import CancelSeatPackConfirmation from "../../cancelSeatPackConfirmation/cancelSeatPackConfirmation";

interface SeatPacksTabProps {
  /** Powers both the empty state (→ Buy Credits) and the past-due CTA (→ Payment details). */
  onNavigateToTab?: (tabId: string) => void;
}

// The POST's own answer, held here rather than derived from the list. See the banner below.
interface CancelOutcome {
  kind: "success" | "error";
  message: string;
}

const statusClass = (row: SeatSubscriptionDto): string => {
  switch (seatPackState(row).kind) {
    case "past_due":
      return styles.statusPastDue;
    case "ending":
      return "";
    case "renewing":
      return styles.statusRenewing;
  }
};

export const SeatPacksTab: React.FC<SeatPacksTabProps> = ({ onNavigateToTab }) => {
  const { data: rows = [], isLoading, isError, error, refetch } = useListSeatSubscriptionsQuery();
  const [cancelSeatSubscription, { isLoading: isCancelling }] = useCancelSeatSubscriptionMutation();

  const [rowToCancel, setRowToCancel] = useState<SeatSubscriptionDto | null>(null);
  const [outcome, setOutcome] = useState<CancelOutcome | null>(null);
  const cancelTriggerRef = useRef<HTMLButtonElement | null>(null);

  const packs = useMemo(() => sortSeatPacks(rows), [rows]);

  const openCancelDialog = (row: SeatSubscriptionDto, trigger: HTMLButtonElement) => {
    cancelTriggerRef.current = trigger;
    setRowToCancel(row);
  };

  const closeCancelDialog = () => {
    setRowToCancel(null);
    cancelTriggerRef.current?.focus();
  };

  const confirmCancel = async () => {
    if (!rowToCancel) return;
    const { id, name } = rowToCancel;

    try {
      const { effectiveAt } = await cancelSeatSubscription(id).unwrap();
      setOutcome({
        kind: "success",
        message: effectiveAt
          ? t("SEAT_PACK_CANCELLED_ON", { name, date: formatDateInIsrael(effectiveAt) })
          : t("SEAT_PACK_CANCELLED_IMMINENTLY", { name }),
      });
    } catch (error) {
      setOutcome({ kind: "error", message: mapSeatCancelError(error) });
      // Re-read on any failure: a 404 means the row on screen is already gone, and every other
      // failure is a good moment to replace what we're showing with fresh truth. Safe to do
      // unconditionally because the outcome above is the POST's own answer, not the list's.
      refetch();
    } finally {
      setRowToCancel(null);
    }
  };

  const renderActions = (row: SeatSubscriptionDto) => {
    const state = seatPackState(row);

    // Cancel is still offered on a past-due pack — a customer who wants out shouldn't be trapped by
    // a failed charge — but demoted to a text action beside the filled "Update card".
    const cancelButton = (
      <button
        type="button"
        className={state.kind === "past_due" ? styles.linkButton : buyStyles.bannerActionButton}
        onClick={(event) => openCancelDialog(row, event.currentTarget)}
      >
        {t("SEAT_PACK_CANCEL")}
      </button>
    );

    switch (state.kind) {
      case "past_due":
        return (
          <>
            <button
              type="button"
              className={buyStyles.bannerActionButton}
              onClick={() => onNavigateToTab?.("payment")}
            >
              {t("UPDATE_CARD")}
            </button>
            {cancelButton}
          </>
        );
      // Already cancelled: no Cancel, and deliberately no reactivate or undo — buying again is the
      // way back, from the Buy Credits tab.
      case "ending":
        return null;
      case "renewing":
        return cancelButton;
    }
  };

  const renderList = () => {
    if (isLoading) return <Loader />;

    // The endpoint 403s for non-admins, and /myAccount is reachable by any signed-in user. Showing
    // them "we couldn't load your seat packs" would be alarming and wrong — they have no packs to
    // see. Same handling as BuyCreditsTab's eligibilityForbidden branch.
    if (isError && (error as { status?: number })?.status === 403) return null;

    // Distinct from the empty state on purpose. Without this, a failed GET renders exactly the same
    // blank screen as "you have no packs", telling a customer whose seats are about to lapse that
    // they have nothing.
    if (isError) {
      return (
        <div className={styles.emptyState}>
          <p>{t("SEAT_PACKS_LOAD_ERROR")}</p>
          <button type="button" className={buyStyles.buyButton} onClick={() => refetch()}>
            {t("RETRY")}
          </button>
        </div>
      );
    }

    if (packs.length === 0) {
      return (
        <div className={styles.emptyState}>
          <p>{t("SEAT_PACKS_EMPTY")}</p>
          <button
            type="button"
            className={buyStyles.buyButton}
            onClick={() => onNavigateToTab?.("buy-credits")}
          >
            {t("BUY_CREDITS")}
          </button>
        </div>
      );
    }

    return (
      <ul className={`list ${styles.seatPackList}`}>
        <li className={`listRow listRow_header ${styles.seatPackRow} ${styles.headerRow}`}>
          <p className="listCell">{t("SEAT_PACK_COLUMN_PACK")}</p>
          <p className="listCell">{t("AMOUNT_UNIT_SEATS")}</p>
          <p className="listCell">{t("SEAT_PACK_COLUMN_PRICE")}</p>
          <p className={`listCell ${styles.statusCell}`}>{t("COLUMN_STATUS")}</p>
          <p className="listCell" />
        </li>
        {packs.map((row, index) => {
          const state = seatPackState(row);
          return (
            <li
              key={row.id}
              className={`listRow ${styles.seatPackRow} ${index % 2 === 0 ? "colored" : ""} ${
                state.kind === "ending" ? styles.endingRow : ""
              }`}
            >
              <p className="listCell" data-label={t("SEAT_PACK_COLUMN_PACK")}>
                {row.name}
              </p>
              <p className="listCell" data-label={t("AMOUNT_UNIT_SEATS")}>
                {row.seats}
              </p>
              {/* An amount followed by a currency code reorders wrongly inside an RTL paragraph —
                  the same fix PaymentDetailsTab applies to the card-number field. */}
              <p className="listCell" data-label={t("SEAT_PACK_COLUMN_PRICE")} dir="ltr">
                {priceLabel(row)}
              </p>
              <p
                className={`listCell ${styles.statusCell} ${statusClass(row)}`}
                data-label={t("COLUMN_STATUS")}
              >
                {state.kind === "ending" && (
                  <span className={`${styles.badge} ${styles.badgeEnding}`}>
                    {t("SEAT_PACK_ENDING_BADGE")}
                  </span>
                )}{" "}
                {seatPackStatusLabel(row)}
              </p>
              <div className={`listCell ${styles.actionsCell}`}>{renderActions(row)}</div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className={buyStyles.wrapper}>
      <div className={buyStyles.section}>
        <div className={buyStyles.sectionHeader}>
          <span className={buyStyles.sectionIcon}>🪑</span>
          <div className={buyStyles.sectionTitleGroup}>
            <span className={buyStyles.sectionTitle}>{t("SEAT_PACKS")}</span>
            <span className={buyStyles.sectionSubtitle}>{t("SEAT_PACKS_DESC")}</span>
          </div>
        </div>
        <div className={buyStyles.sectionBody}>
          {/* Above the loading/error/empty switch, never inside it: the invalidation fires a GET
              after the POST succeeds, and if that GET fails on a network blip the naive structure
              would flip the tab to its error state and swallow the one confirmation the user needs
              for an operation that actually worked. Both messages showing is the correct outcome.

              Inline rather than a popup because the account screen is itself a modal — stacking a
              second one over it would hide the very row that just changed. */}
          {outcome && (
            <div
              className={`${buyStyles.statusBanner} ${
                outcome.kind === "success" ? buyStyles.statusBannerSuccess : buyStyles.statusBannerError
              }`}
              role="status"
              aria-live="polite"
            >
              <span>{outcome.message}</span>
              <button
                type="button"
                className={buyStyles.statusBannerDismiss}
                aria-label={t("DISMISS")}
                onClick={() => setOutcome(null)}
              >
                ×
              </button>
            </div>
          )}

          {renderList()}
        </div>
      </div>

      {rowToCancel && (
        <Modal onClose={closeCancelDialog}>
          <CancelSeatPackConfirmation
            row={rowToCancel}
            isCancelling={isCancelling}
            onClose={closeCancelDialog}
            onConfirm={confirmCancel}
          />
        </Modal>
      )}
    </div>
  );
};
